import moment from 'moment';
import { trackEvent } from '../../Analytics/TrackedClick';
import { platform, RallyEventRecEvents, SubscriptionTypes } from '../../Analytics/constants';

const VOICEFLOW_WIDGET_SRC = 'https://cdn.voiceflow.com/widget-next/bundle.mjs';
const VOICEFLOW_RUNTIME_URL = 'https://general-runtime.voiceflow.com';
const VOICEFLOW_VOICE_URL = 'https://runtime-api.voiceflow.com';

let voiceflowScriptLoadingPromise = null;

export const getVoiceflowWidgetSource = () => VOICEFLOW_WIDGET_SRC;
export const getVoiceflowRuntimeUrl = () => VOICEFLOW_RUNTIME_URL;
export const getVoiceflowVoiceUrl = () => VOICEFLOW_VOICE_URL;

export const loadVoiceflowScript = () => {
    if (window.voiceflow?.chat) {
        return Promise.resolve();
    }

    if (voiceflowScriptLoadingPromise !== null) {
        return voiceflowScriptLoadingPromise;
    }

    voiceflowScriptLoadingPromise = new Promise((resolve, reject) => {
        const handleScriptLoaded = (event) => {
            resolve(event);
        };
        const handleScriptError = (error) => {
            reject(error);
        };
        const existingScript = document.querySelector(`script[src="${VOICEFLOW_WIDGET_SRC}"]`);
        if (existingScript) {
            const isScriptLoaded = existingScript.getAttribute('data-utr-voiceflow-loaded') === 'true' || window.voiceflow?.chat;
            if (isScriptLoaded) {
                resolve();
                return;
            }
            existingScript.addEventListener('load', handleScriptLoaded);
            existingScript.addEventListener('error', handleScriptError);
            return;
        }

        const scriptTag = document.createElement('script');
        const firstScript = document.getElementsByTagName('script')[0];

        scriptTag.onload = (event) => {
            scriptTag.setAttribute('data-utr-voiceflow-loaded', 'true');
            handleScriptLoaded(event);
        };
        scriptTag.onerror = handleScriptError;
        scriptTag.src = VOICEFLOW_WIDGET_SRC;
        scriptTag.type = 'text/javascript';

        firstScript.parentNode.insertBefore(scriptTag, firstScript);
    });

    voiceflowScriptLoadingPromise = voiceflowScriptLoadingPromise.catch((error) => {
        voiceflowScriptLoadingPromise = null;
        throw error;
    });

    return voiceflowScriptLoadingPromise;
};

export const parseVoiceflowWidgetMessage = (messageData) => {
    if (!messageData) {
        return null;
    }

    if (typeof messageData === 'string') {
        try {
            return JSON.parse(messageData);
        } catch (error) {
            if (!(error instanceof SyntaxError)) {
                // eslint-disable-next-line no-console
                console.warn('Unexpected Voiceflow widget parse error', error);
            }
            return null;
        }
    }

    return messageData;
};

export const isVoiceflowCloseEvent = (messageData) => {
    const parsedMessage = parseVoiceflowWidgetMessage(messageData);
    return parsedMessage?.type === 'voiceflow:close';
};

export const shouldCloseFromVoiceflowMessage = (messageData) => {
    const parsedMessage = parseVoiceflowWidgetMessage(messageData);
    const isMalformed = typeof messageData === 'string' && !parsedMessage;
    return {
        isMalformed,
        shouldClose: !isMalformed && parsedMessage?.type === 'voiceflow:close',
    };
};

export const cleanupVoiceflowObserver = (observer) => {
    if (observer) {
        observer.disconnect();
    }
    return null;
};

const SPARKLE_PATH = 'M6.58973 3.9352C6.77063 3.90595 6.86601 3.80702 6.87589 3.63841C6.88577 3.46979 6.80261 3.36041 6.62636 3.31031C6.04684 3.17693 5.59597 3.05605 5.2738 2.94766C4.94172 2.83869 4.69629 2.69497 4.53751 2.5165C4.37873 2.33803 4.2596 2.07728 4.18014 1.73425C4.10068 1.39122 4.01918 0.913699 3.93557 0.301703C3.91685 0.11151 3.81819 0.011201 3.63961 0.000769926C3.47095 -0.00908166 3.36153 0.0741082 3.31139 0.250327C3.1464 0.857754 3.0094 1.32747 2.90036 1.65949C2.78198 1.98102 2.63354 2.22116 2.45501 2.37992C2.26713 2.52819 2.00687 2.6374 1.67425 2.70755C1.34163 2.77769 0.884148 2.85547 0.301764 2.94088C0.110943 2.96954 0.0105979 3.06818 0.000721791 3.2368C-0.00915436 3.40542 0.0789901 3.51507 0.265164 3.56576C0.833605 3.71839 1.28388 3.84919 1.61596 3.95817C1.93871 4.05664 2.17946 4.19511 2.33824 4.37358C2.4976 4.54213 2.61701 4.79792 2.69647 5.14096C2.77593 5.48399 2.85743 5.96151 2.94104 6.5735C2.95977 6.7637 3.05846 6.864 3.23703 6.87443C3.41561 6.88486 3.52525 6.79673 3.56605 6.61002C3.73046 6.01251 3.87217 5.54803 3.99113 5.21659C4.11067 4.87523 4.25943 4.63012 4.43738 4.48127C4.61591 4.3225 4.87118 4.21301 5.20322 4.15278C5.53584 4.08264 5.998 4.01011 6.58973 3.9352Z';

export const injectVoiceflowHeaderTitle = ({
    target,
    retryCount = 0,
    maxRetries = 20,
}) => {
    const shadowRoot = target?.shadowRoot;

    if (!shadowRoot) {
        if (retryCount < maxRetries) {
            setTimeout(() => {
                injectVoiceflowHeaderTitle({ target, retryCount: retryCount + 1, maxRetries });
            }, 100);
        }
        return;
    }

    const ensureTitle = () => {
        const titleEl = shadowRoot.querySelector('.vfrc-header--title');
        if (!titleEl) {
            return false;
        }

        if (shadowRoot.getElementById('utr-voiceflow-header-title')) {
            return true;
        }

        const svgNs = 'http://www.w3.org/2000/svg';

        const wrapper = document.createElement('span');
        wrapper.id = 'utr-voiceflow-header-title';
        wrapper.style.fontSize = '16px';
        wrapper.style.color = 'white';
        wrapper.style.display = 'inline-flex';
        wrapper.style.alignItems = 'center';

        const primaryText = document.createElement('span');
        primaryText.style.display = 'inline-flex';
        primaryText.style.alignItems = 'baseline';

        const chatWith = document.createElement('span');
        chatWith.textContent = 'Chat with';
        chatWith.style.fontWeight = 'normal';

        const rally = document.createElement('span');
        rally.textContent = 'Rally';
        rally.style.marginLeft = '4px';
        rally.style.fontWeight = '600';

        primaryText.appendChild(chatWith);
        primaryText.appendChild(rally);

        const starSpan = document.createElement('span');
        starSpan.style.width = '6.58px';
        starSpan.style.height = '6.58px';
        starSpan.style.display = 'inline-flex';
        starSpan.style.alignItems = 'center';
        starSpan.style.justifyContent = 'center';
        starSpan.style.position = 'relative';
        starSpan.style.top = '-5px';
        starSpan.style.marginLeft = '0';
        starSpan.style.marginRight = '2px';
        starSpan.style.flexShrink = '0';

        const starSvg = document.createElementNS(svgNs, 'svg');
        starSvg.setAttribute('width', '100%');
        starSvg.setAttribute('height', '100%');
        starSvg.setAttribute('viewBox', '0 0 7 7');
        starSvg.setAttribute('fill', 'none');
        starSvg.setAttribute('aria-hidden', 'true');

        const starPath = document.createElementNS(svgNs, 'path');
        starPath.setAttribute('d', SPARKLE_PATH);
        starPath.setAttribute('fill', 'white');

        starSvg.appendChild(starPath);
        starSpan.appendChild(starSvg);
        wrapper.appendChild(primaryText);
        wrapper.appendChild(starSpan);

        titleEl.appendChild(wrapper);
        return true;
    };

    const titleAdded = ensureTitle();
    if (!titleAdded) {
        if (retryCount < maxRetries) {
            setTimeout(() => {
                injectVoiceflowHeaderTitle({ target, retryCount: retryCount + 1, maxRetries });
            }, 100);
        }
        return;
    }

    const observer = new MutationObserver(() => {
        ensureTitle();
    });
    observer.observe(shadowRoot, { childList: true, subtree: true });
};

export const injectVoiceflowHeaderCloseButton = ({
    target,
    onClose,
    existingObserver = null,
    setObserver,
    retryCount = 0,
    maxRetries = 20,
}) => {
    const shadowRoot = target?.shadowRoot;

    if (!shadowRoot) {
        if (retryCount < maxRetries) {
            setTimeout(() => {
                injectVoiceflowHeaderCloseButton({
                    target,
                    onClose,
                    existingObserver,
                    setObserver,
                    retryCount: retryCount + 1,
                    maxRetries,
                });
            }, 100);
        }
        return existingObserver;
    }

    const ensureButton = () => {
        const header = shadowRoot.querySelector('.vfrc-header');
        if (!header) {
            return false;
        }

        const existingButton = shadowRoot.getElementById('utr-voiceflow-close-button');
        if (existingButton) {
            return true;
        }

        const refreshControl = header.querySelector('[role="button"]') || header.querySelector('button');
        const headerControls = refreshControl?.parentElement || header;

        const closeButton = document.createElement('button');
        closeButton.id = 'utr-voiceflow-close-button';
        closeButton.type = 'button';
        closeButton.setAttribute('aria-label', 'Close chat');
        closeButton.setAttribute('title', 'Close chat');
        closeButton.style.border = '0';
        closeButton.style.background = 'transparent';
        closeButton.style.color = '#ffffff';
        closeButton.style.cursor = 'pointer';
        closeButton.style.marginLeft = '4px';
        closeButton.style.padding = '0';
        closeButton.style.display = 'inline-flex';
        closeButton.style.alignItems = 'center';
        closeButton.style.justifyContent = 'center';
        closeButton.style.borderRadius = '4px';
        closeButton.style.transition = 'background-color 0.15s ease';

        if (refreshControl) {
            const refreshStyles = window.getComputedStyle(refreshControl);
            closeButton.style.width = refreshStyles.width;
            closeButton.style.height = refreshStyles.height;
            closeButton.style.padding = refreshStyles.padding;
            closeButton.style.color = refreshStyles.color;
            closeButton.style.borderRadius = refreshStyles.borderRadius;
        }

        const refreshIcon = refreshControl?.querySelector('svg, span, i');
        const refreshSvg = refreshIcon?.tagName?.toLowerCase() === 'svg'
            ? refreshIcon
            : refreshIcon?.querySelector?.('svg');

        if (refreshSvg) {
            const svgNs = 'http://www.w3.org/2000/svg';
            const closeSvg = refreshSvg.cloneNode(false);
            const viewBox = refreshSvg.getAttribute('viewBox') || '0 0 24 24';
            const viewBoxParts = viewBox.split(' ').map(Number);
            const minX = Number.isFinite(viewBoxParts[0]) ? viewBoxParts[0] : 0;
            const minY = Number.isFinite(viewBoxParts[1]) ? viewBoxParts[1] : 0;
            const width = Number.isFinite(viewBoxParts[2]) ? viewBoxParts[2] : 24;
            const height = Number.isFinite(viewBoxParts[3]) ? viewBoxParts[3] : 24;
            const insetX = width * 0.28;
            const insetY = height * 0.28;

            const strokeNode = refreshSvg.querySelector('[stroke-width]');
            const strokeWidth = strokeNode?.getAttribute('stroke-width') || refreshSvg.getAttribute('stroke-width') || '2';
            const strokeLineCap = strokeNode?.getAttribute('stroke-linecap') || refreshSvg.getAttribute('stroke-linecap') || 'round';
            const strokeLineJoin = strokeNode?.getAttribute('stroke-linejoin') || refreshSvg.getAttribute('stroke-linejoin') || 'round';

            closeSvg.setAttribute('aria-hidden', 'true');
            closeSvg.setAttribute('focusable', 'false');

            const lineOne = document.createElementNS(svgNs, 'line');
            lineOne.setAttribute('x1', String(minX + insetX));
            lineOne.setAttribute('y1', String(minY + insetY));
            lineOne.setAttribute('x2', String(minX + width - insetX));
            lineOne.setAttribute('y2', String(minY + height - insetY));
            lineOne.setAttribute('stroke', 'currentColor');
            lineOne.setAttribute('stroke-width', strokeWidth);
            lineOne.setAttribute('stroke-linecap', strokeLineCap);
            lineOne.setAttribute('stroke-linejoin', strokeLineJoin);

            const lineTwo = document.createElementNS(svgNs, 'line');
            lineTwo.setAttribute('x1', String(minX + width - insetX));
            lineTwo.setAttribute('y1', String(minY + insetY));
            lineTwo.setAttribute('x2', String(minX + insetX));
            lineTwo.setAttribute('y2', String(minY + height - insetY));
            lineTwo.setAttribute('stroke', 'currentColor');
            lineTwo.setAttribute('stroke-width', strokeWidth);
            lineTwo.setAttribute('stroke-linecap', strokeLineCap);
            lineTwo.setAttribute('stroke-linejoin', strokeLineJoin);

            closeSvg.appendChild(lineOne);
            closeSvg.appendChild(lineTwo);
            closeButton.appendChild(closeSvg);
        } else {
            const closeIcon = document.createElement('span');
            closeIcon.textContent = '\u00D7';
            closeIcon.style.fontSize = '18px';
            closeIcon.style.lineHeight = '1';
            closeButton.appendChild(closeIcon);
        }

        closeButton.addEventListener('mouseenter', () => {
            closeButton.style.backgroundColor = 'rgba(255, 255, 255, 0.16)';
        });
        closeButton.addEventListener('mouseleave', () => {
            closeButton.style.backgroundColor = 'transparent';
        });

        headerControls.appendChild(closeButton);
        closeButton.addEventListener('click', onClose);
        return true;
    };

    const buttonAdded = ensureButton();
    if (!buttonAdded) {
        if (retryCount < maxRetries) {
            setTimeout(() => {
                injectVoiceflowHeaderCloseButton({
                    target,
                    onClose,
                    existingObserver,
                    setObserver,
                    retryCount: retryCount + 1,
                    maxRetries,
                });
            }, 100);
        }
        return existingObserver;
    }

    if (!existingObserver) {
        const observer = new MutationObserver(() => {
            ensureButton();
        });
        observer.observe(shadowRoot, { childList: true, subtree: true });
        if (setObserver) {
            setObserver(observer);
        }
        return observer;
    }

    return existingObserver;
};

export const getSubscriptionState = (user) => {
    if (user?.isPowered) { return 'active'; }
    if (!user?.isPowered && user?.hasPastDueSubscription) { return 'past_due'; }
    return 'free';
};

const RALLY_OPEN_PAYMENT_ACTION = 'rally_open_payment_modal';
const RALLY_OPEN_SUBSCRIPTION_ACTION = 'rally_open_subscription';
const RALLY_EVENT_CAROUSEL_TRACE_TYPE = 'event_carousel';

const createRallyPaymentExtension = (onOpenPaymentModal) => ({
    name: 'RallyPaymentModal',
    type: 'effect',
    match: ({ trace }) => trace.type === RALLY_OPEN_PAYMENT_ACTION || trace.payload?.name === RALLY_OPEN_PAYMENT_ACTION,
    effect: ({ trace }) => {
        onOpenPaymentModal(trace.payload);
    },
});

const createRallySubscriptionExtension = (onOpenSubscriptionPage) => ({
    name: 'RallySubscriptionPage',
    type: 'effect',
    match: ({ trace }) => trace.type === RALLY_OPEN_SUBSCRIPTION_ACTION || trace.payload?.name === RALLY_OPEN_SUBSCRIPTION_ACTION,
    effect: ({ trace }) => {
        onOpenSubscriptionPage(trace.payload);
    },
});

const RALLY_EVENTS_RECOMMENDATIONS_TRACE_TYPE = 'events_recommendations';

const createEventRecommendationsTelemetryExtension = ({ memberId, isPowered, location } = {}) => ({
    name: 'EventRecommendationsTelemetry',
    type: 'effect',
    match: ({ trace }) => trace.type === RALLY_EVENTS_RECOMMENDATIONS_TRACE_TYPE,
    effect: ({ trace }) => {
        const { events, status, invoked_at: invokedAt } = trace.payload || {};
        const userType = isPowered ? SubscriptionTypes.POWER : SubscriptionTypes.FREE;

        // invoked_at is captured by the function tool at the start of its own
        // execution (before the fetch), so it reflects when the tool was
        // actually invoked — closer to "requested" than whenever this effect
        // happens to run client-side after the trace round-trips back. Falls
        // back to now only if a trace ever arrives without it.
        const requestedAt = typeof invokedAt === 'number' ? moment.utc(invokedAt) : moment.utc();

        // Fires regardless of status — a failed API call is still a request.
        trackEvent(RallyEventRecEvents.RALLY_EVENT_REC_REQUESTED, {
            user_id: memberId,
            platform,
            user_type: userType,
            timestamp: requestedAt,
        });

        if (status !== 'complete') {
            return;
        }

        // Real shape: payload.events is { memberid, eventRecs: [...] }, not a
        // bare array — the function tool's error branch sends a bare
        // events: [], but that path already returned above via the status
        // check, so eventRecs is the only shape reachable here.
        const eventRecs = events?.eventRecs || [];

        if (eventRecs.length > 0) {
            trackEvent(RallyEventRecEvents.RALLY_EVENT_REC_RETURNED, {
                user_id: memberId,
                platform,
                user_type: userType,
                result_count: eventRecs.length,
                location,
            });
        } else {
            trackEvent(RallyEventRecEvents.RALLY_EVENT_REC_ZERO_RESULTS, {
                user_id: memberId,
                platform,
                user_type: userType,
                location,
            });
        }
    },
});

const RALLY_EVENT_CARD_STYLE_ID = 'rally-event-card-styles';

// This card's own display choice — not the system's canonical UTR-matching
// threshold (the consultative note elsewhere uses ±2 UTR for that). The API
// returns registrant counts at ±0.5/±1.0/±1.5/±2.0; ±1.0 is shown here as a
// single representative band since the card has no room for all four.
const CARD_UTR_PROXIMITY_DISPLAY_WINDOW = 1.0;

// Mirrors assets/myutr-uikit/_variables.scss — duplicated here because this
// <style> block is injected directly into the Voiceflow widget's own DOM,
// which doesn't load the app's compiled stylesheet, so the SCSS variables
// themselves aren't reachable from this file.
const RALLY_EVENT_CARD_COLORS = {
    BORDER: '#ddd', // $rally-card-border
    BACKGROUND: '#fff', // $white-color
    TEAL: '#00bab6', // $light-teal
    LIGHT_GRAY: '#a5b2bb', // $light-gray
    DARK_TEXT: '#2b3339', // $dark-blue / $body-text-black
    BUTTON: '#387dff', // $rally-card-button-blue
    BUTTON_HOVER: '#2f68db', // $rally-card-button-blue-hover
};

// Appended into the same local render scope as the card/carousel content
// (mirroring the reference implementation's element.appendChild(style))
// rather than document.head — the Voiceflow widget renders inside a shadow
// root (see injectVoiceflowHeaderTitle's use of target.shadowRoot), and
// styles appended to the outer document's <head> never cross that boundary.
const injectEventCardStyles = (target) => {
    if (target.querySelector(`#${RALLY_EVENT_CARD_STYLE_ID}`)) {
        return;
    }
    const style = document.createElement('style');
    style.id = RALLY_EVENT_CARD_STYLE_ID;
    style.textContent = `
        .vf-event-card { flex: 0 0 200px; border: 1px solid ${RALLY_EVENT_CARD_COLORS.BORDER}; border-radius: 8px; overflow: hidden; background: ${RALLY_EVENT_CARD_COLORS.BACKGROUND}; font-family: sans-serif; }
        .vf-event-card-body { padding: 12px; }
        .vf-event-card-tags { margin: 0 0 6px 0; }
        .vf-event-card-tag { display: inline-block; font-size: 10px; font-weight: 600; text-transform: uppercase; line-height: 1; padding: 3px 6px; border-radius: 10px; border: 1px solid ${RALLY_EVENT_CARD_COLORS.TEAL}; color: ${RALLY_EVENT_CARD_COLORS.TEAL}; margin-right: 4px; }
        .vf-event-card-tag-junior { border-color: ${RALLY_EVENT_CARD_COLORS.LIGHT_GRAY}; color: ${RALLY_EVENT_CARD_COLORS.LIGHT_GRAY}; }
        .vf-event-card-title { font-size: 14px; font-weight: 600; margin: 0 0 6px 0; color: ${RALLY_EVENT_CARD_COLORS.DARK_TEXT}; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
        .vf-event-card-meta { font-size: 12px; color: ${RALLY_EVENT_CARD_COLORS.DARK_TEXT}; margin: 0 0 6px 0; }
        .vf-event-card button { width: 100%; padding: 8px; background: ${RALLY_EVENT_CARD_COLORS.BUTTON}; color: ${RALLY_EVENT_CARD_COLORS.BACKGROUND}; border: none; border-radius: 6px; font-size: 13px; font-weight: 600; cursor: pointer; }
        .vf-event-card button:hover { background: ${RALLY_EVENT_CARD_COLORS.BUTTON_HOVER}; }
        .vf-event-card button:disabled { opacity: 0.35; cursor: not-allowed; }
    `;
    target.appendChild(style);
};

export const createEventCard = (event, { isPowered, styleTarget, testIdPrefix: parentTestIdPrefix }) => {
    injectEventCardStyles(styleTarget);

    const testIdPrefix = parentTestIdPrefix
        ? `${parentTestIdPrefix}.event-card-${event.eventid}`
        : `rally.event-card-${event.eventid}`;

    const card = document.createElement('div');
    card.className = 'vf-event-card';
    card.setAttribute('data-testid', testIdPrefix);

    const body = document.createElement('div');
    body.className = 'vf-event-card-body';

    const tags = [
        event.has_color_ball && { label: 'Color Ball', className: '', testId: 'tag-color-ball' },
        event.is_junior_event && { label: 'Junior', className: 'vf-event-card-tag-junior', testId: 'tag-junior' },
    ].filter(Boolean);
    if (tags.length) {
        const tagsRow = document.createElement('div');
        tagsRow.className = 'vf-event-card-tags';
        tagsRow.setAttribute('data-testid', `${testIdPrefix}.tags-wrapper`);
        tags.forEach(({ label, className, testId }) => {
            const tag = document.createElement('span');
            tag.className = `vf-event-card-tag ${className}`.trim();
            tag.textContent = label;
            tag.setAttribute('data-testid', `${testIdPrefix}.${testId}`);
            tagsRow.appendChild(tag);
        });
        body.appendChild(tagsRow);
    }

    const title = document.createElement('p');
    title.className = 'vf-event-card-title';
    title.textContent = event.eventname;
    title.setAttribute('data-testid', `${testIdPrefix}.title`);
    body.appendChild(title);

    const metaParts = [
        typeof event.distance_to_event === 'number' && `${Math.round(event.distance_to_event)} mi away`,
        typeof event.total_registrants_in_event === 'number' && `${event.total_registrants_in_event} registered`,
    ].filter(Boolean);
    if (metaParts.length) {
        const meta = document.createElement('p');
        meta.className = 'vf-event-card-meta';
        meta.textContent = metaParts.join(' • ');
        meta.setAttribute('data-testid', `${testIdPrefix}.meta`);
        body.appendChild(meta);
    }

    if (isPowered && typeof event.registrants_within_one === 'number') {
        const powerMeta = document.createElement('p');
        powerMeta.className = 'vf-event-card-meta';
        powerMeta.textContent = `${event.registrants_within_one} registered within ${CARD_UTR_PROXIMITY_DISPLAY_WINDOW.toFixed(1)} UTR`;
        powerMeta.setAttribute('data-testid', `${testIdPrefix}.power-meta`);
        body.appendChild(powerMeta);
    }

    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = 'View Event';
    button.setAttribute('data-testid', `${testIdPrefix}.view-button`);
    body.appendChild(button);

    card.appendChild(body);
    return card;
};

const RALLY_EVENT_CAROUSEL_STYLE_ID = 'rally-event-carousel-styles';
const RALLY_VOICEFLOW_CHAT_FRAME_ID = 'voiceflow-chat-frame';
const RALLY_VOICEFLOW_DIALOG_SCROLL_CONTAINER_SELECTOR = '.vfrc-chat-dialog__container';
const RALLY_EVENT_CAROUSEL_FALLBACK_WIDTH_PX = 320;
const RALLY_EVENT_CAROUSEL_MIN_WIDTH_PX = 200;
// Fallback-only guess, used when .vfrc-chat-dialog__container can't be found.
const RALLY_EVENT_CAROUSEL_PANEL_PADDING_BUFFER_PX = 20;
// Matches the inset regular text message bubbles keep from the real clip
// edge (measured live: bubble right edge sat 20px inside the true
// boundary), so the carousel's right edge lines up with the text messages
// around it instead of just avoiding overflow with an arbitrary margin.
const RALLY_EVENT_CAROUSEL_RIGHT_EDGE_BUFFER_PX = 20;
const RALLY_EVENT_CAROUSEL_CARD_GAP_PX = 12;
const RALLY_EVENT_CAROUSEL_DRAG_CLICK_SUPPRESSION_PX = 8;
const RALLY_EVENT_CAROUSEL_MIN_THUMB_WIDTH_PX = 20;
// Long enough to absorb an accidental rapid double-click/double-tap on
// "View Event" (which would otherwise open two tabs and double-fire
// telemetry); short enough that switching back from the opened tab to view
// a different event in the same carousel doesn't feel broken.
const RALLY_EVENT_CAROUSEL_CLICK_DEBOUNCE_MS = 600;

// Mirrors assets/myutr-uikit/_variables.scss — see createEventCard's
// RALLY_EVENT_CARD_COLORS comment for why these can't be imported directly.
const RallyEventCarouselColors = {
    NAV_BORDER: '#ddd', // $rally-card-border
    NAV_BACKGROUND: '#fff', // $white-color
    NAV_ICON: '#2b3339', // $dark-blue
    SCROLLBAR_TRACK: '#e6e6e6', // $rally-carousel-scrollbar-track
    SCROLLBAR_THUMB: '#b3b3b3', // $rally-carousel-scrollbar-thumb
    SCROLLBAR_THUMB_DRAGGING: '#888', // $rally-carousel-scrollbar-thumb-dragging
};

// Appended into contextElement (the local render scope), not document.head
// — same shadow-DOM-boundary reasoning as injectEventCardStyles above.
const injectEventCarouselStyles = (target) => {
    if (target.querySelector(`#${RALLY_EVENT_CAROUSEL_STYLE_ID}`)) {
        return;
    }
    const style = document.createElement('style');
    style.id = RALLY_EVENT_CAROUSEL_STYLE_ID;
    style.textContent = `
        .vf-event-carousel-wrapper { position: relative; max-width: 100%; min-width: 0; overflow: hidden; box-sizing: border-box; touch-action: pan-y; }
        .vf-event-carousel-track { display: flex; gap: ${RALLY_EVENT_CAROUSEL_CARD_GAP_PX}px; padding: 8px 0; box-sizing: border-box; width: max-content; cursor: grab; }
        .vf-event-carousel-track.vf-event-carousel-dragging { cursor: grabbing; }
        .vf-event-carousel-nav { display: none; position: absolute; top: 50%; transform: translateY(-50%); width: 28px; height: 28px; border-radius: 50%; border: 1px solid ${RallyEventCarouselColors.NAV_BORDER}; background: ${RallyEventCarouselColors.NAV_BACKGROUND}; color: ${RallyEventCarouselColors.NAV_ICON}; font-size: 14px; line-height: 1; cursor: pointer; align-items: center; justify-content: center; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15); z-index: 1; }
        .vf-event-carousel-nav-prev { left: 4px; }
        .vf-event-carousel-nav-next { right: 4px; }
        .vf-event-carousel-nav:disabled { opacity: 0.35; cursor: not-allowed; }
        .vf-event-carousel-scrollbar-track { position: relative; height: 4px; margin: 2px 8px 6px; background: ${RallyEventCarouselColors.SCROLLBAR_TRACK}; border-radius: 2px; cursor: pointer; }
        .vf-event-carousel-scrollbar-thumb { position: absolute; top: 0; left: 0; height: 4px; min-width: ${RALLY_EVENT_CAROUSEL_MIN_THUMB_WIDTH_PX}px; background: ${RallyEventCarouselColors.SCROLLBAR_THUMB}; border-radius: 2px; cursor: grab; }
        .vf-event-carousel-scrollbar-thumb.vf-event-carousel-dragging { cursor: grabbing; background: ${RallyEventCarouselColors.SCROLLBAR_THUMB_DRAGGING}; }
    `;
    target.appendChild(style);
};

// contextElement is the live-mounted DOM node this carousel renders into
// (Voiceflow's own trace-render `element`, in production) — needed at
// construction time, not just for insertion, because getPanelWidthPx below
// measures against its real ancestor scroll container immediately after
// the wrapper is appended, before anything else runs.
// Older Rally messages (and their carousels) stay mounted in the chat
// transcript as the conversation continues, so a static testid would
// collide the moment a user asks for events more than once in a session.
let rallyEventCarouselInstanceCount = 0;

export const createEventCarousel = (events, contextElement, { isPowered, onViewEventClick } = {}) => {
    injectEventCarouselStyles(contextElement);
    rallyEventCarouselInstanceCount += 1;
    const testIdPrefix = `rally.event-carousel-${rallyEventCarouselInstanceCount}`;

    // .vfrc-chat-dialog__container is the real, genuinely overflow:hidden
    // ancestor that clips message content — its right edge is the true
    // boundary. The wrapper's own LEFT position is stable (fixed by
    // ancestor padding/avatar layout regardless of how wide our content
    // grows), so "container's right edge minus wrapper's left position"
    // gives the exact available width — no guessed buffer needed.
    const getPanelWidthPx = (wrapperEl) => {
        const scrollContainer = contextElement.closest(RALLY_VOICEFLOW_DIALOG_SCROLL_CONTAINER_SELECTOR);
        const wrapperLeft = wrapperEl?.getBoundingClientRect().left;
        if (scrollContainer && typeof wrapperLeft === 'number' && wrapperLeft > 0) {
            const containerRect = scrollContainer.getBoundingClientRect();
            const containerRight = containerRect.left + scrollContainer.clientWidth;
            const available = containerRight - wrapperLeft - RALLY_EVENT_CAROUSEL_RIGHT_EDGE_BUFFER_PX;
            if (available > 0) {
                return Math.max(available, RALLY_EVENT_CAROUSEL_MIN_WIDTH_PX);
            }
        }

        // #voiceflow-chat-frame is OUR OWN container in the regular page
        // DOM — used only as a fallback when the real scroll container
        // above can't be found.
        const panel = document.getElementById(RALLY_VOICEFLOW_CHAT_FRAME_ID);
        const measured = panel?.getBoundingClientRect().width;
        const withBuffer = (measured || RALLY_EVENT_CAROUSEL_FALLBACK_WIDTH_PX) - RALLY_EVENT_CAROUSEL_PANEL_PADDING_BUFFER_PX;
        return Math.max(withBuffer, RALLY_EVENT_CAROUSEL_MIN_WIDTH_PX);
    };

    const wrapper = document.createElement('div');
    wrapper.className = 'vf-event-carousel-wrapper';
    wrapper.setAttribute('data-testid', `${testIdPrefix}.wrapper`);

    // Inserted before building the track so wrapper.getBoundingClientRect()
    // inside getPanelWidthPx() reflects its real position in the message
    // row, not something skewed by our own not-yet-built content.
    contextElement.appendChild(wrapper);
    wrapper.style.width = `${getPanelWidthPx(wrapper)}px`;

    // Driven by transform: translateX(...) on this track rather than native
    // overflow-x scrolling. .vfrc-chat-dialog (an ancestor we don't control)
    // carries a CSS transform, which promotes it to its own GPU-composited
    // layer; native scroll-position changes deep inside that layer proved
    // unreliable to repaint. Directly setting transform on our own track
    // sidesteps that.
    const track = document.createElement('div');
    track.className = 'vf-event-carousel-track';
    track.setAttribute('data-testid', `${testIdPrefix}.track`);

    events.forEach((evt) => {
        const card = createEventCard(evt, { isPowered, styleTarget: contextElement, testIdPrefix });
        if (onViewEventClick) {
            const button = card.querySelector('button');
            if (button) {
                button.addEventListener('click', () => {
                    // A link-less event has nothing to debounce — no tab
                    // opens, so there's no risk of a double-click firing two
                    // navigations. Disabling here would just leave the whole
                    // carousel looking dead for no reason.
                    if (!evt.event_link) {
                        onViewEventClick(evt);
                        return;
                    }
                    // Disabled here, synchronously on click, rather than left
                    // to onViewEventClick — prevents a rapid double-click
                    // (any card) from opening a second tab or double-firing
                    // telemetry. Re-enabled after the debounce window so the
                    // carousel stays usable for viewing other events.
                    const buttons = track.querySelectorAll('button');
                    buttons.forEach((otherButton) => {
                        otherButton.disabled = true;
                    });
                    // finally, not just a trailing call — if onViewEventClick
                    // throws, the re-enable must still run or the carousel
                    // reverts to permanently disabled, the exact bug this is
                    // fixing.
                    try {
                        onViewEventClick(evt);
                    } finally {
                        setTimeout(() => {
                            buttons.forEach((otherButton) => {
                                otherButton.disabled = false;
                            });
                        }, RALLY_EVENT_CAROUSEL_CLICK_DEBOUNCE_MS);
                    }
                });
            }
        }
        track.appendChild(card);
    });

    const prevButton = document.createElement('button');
    prevButton.type = 'button';
    prevButton.className = 'vf-event-carousel-nav vf-event-carousel-nav-prev';
    prevButton.setAttribute('aria-label', 'Previous events');
    prevButton.setAttribute('data-testid', `${testIdPrefix}.nav-prev`);
    prevButton.textContent = '‹';

    const nextButton = document.createElement('button');
    nextButton.type = 'button';
    nextButton.className = 'vf-event-carousel-nav vf-event-carousel-nav-next';
    nextButton.setAttribute('aria-label', 'Next events');
    nextButton.setAttribute('data-testid', `${testIdPrefix}.nav-next`);
    nextButton.textContent = '›';

    const scrollbarTrack = document.createElement('div');
    scrollbarTrack.className = 'vf-event-carousel-scrollbar-track';
    scrollbarTrack.setAttribute('data-testid', `${testIdPrefix}.scrollbar-track`);
    const scrollbarThumb = document.createElement('div');
    scrollbarThumb.className = 'vf-event-carousel-scrollbar-thumb';
    scrollbarThumb.setAttribute('data-testid', `${testIdPrefix}.scrollbar-thumb`);
    scrollbarTrack.appendChild(scrollbarThumb);

    wrapper.appendChild(track);
    wrapper.appendChild(prevButton);
    wrapper.appendChild(nextButton);
    wrapper.appendChild(scrollbarTrack);

    let offset = 0;

    const getMinOffset = () => Math.min(0, wrapper.getBoundingClientRect().width - track.getBoundingClientRect().width);

    const getScrollbarMetrics = () => {
        const wrapperWidth = wrapper.getBoundingClientRect().width;
        const trackWidth = track.getBoundingClientRect().width;
        const maxOffsetMagnitude = Math.max(0, trackWidth - wrapperWidth);
        const barWidth = scrollbarTrack.getBoundingClientRect().width;
        const thumbWidth = Math.max(RALLY_EVENT_CAROUSEL_MIN_THUMB_WIDTH_PX, (wrapperWidth / trackWidth) * barWidth);
        return { maxOffsetMagnitude, barWidth, thumbWidth };
    };

    const updateScrollbar = () => {
        const { maxOffsetMagnitude, barWidth, thumbWidth } = getScrollbarMetrics();
        if (maxOffsetMagnitude <= 0) {
            scrollbarTrack.style.display = 'none';
            return;
        }
        scrollbarTrack.style.display = '';
        const scrollFraction = -offset / maxOffsetMagnitude;
        const thumbLeft = scrollFraction * (barWidth - thumbWidth);
        scrollbarThumb.style.width = `${thumbWidth}px`;
        scrollbarThumb.style.transform = `translateX(${thumbLeft}px)`;
    };

    const applyOffset = (nextOffset) => {
        const minOffset = getMinOffset();
        offset = Math.min(0, Math.max(nextOffset, minOffset));
        track.style.transform = `translateX(${offset}px)`;
        prevButton.disabled = offset >= 0;
        nextButton.disabled = offset <= minOffset;
        updateScrollbar();
    };

    // Measures the actual rendered first card rather than assuming a fixed
    // width, so paging doesn't silently drift out of sync if
    // createEventCard's own card width ever changes.
    const getStepPx = () => {
        const firstCardWidth = track.firstElementChild?.getBoundingClientRect().width || RALLY_EVENT_CAROUSEL_MIN_WIDTH_PX;
        return firstCardWidth + RALLY_EVENT_CAROUSEL_CARD_GAP_PX;
    };

    // Only used by the (currently hidden) prev/next buttons — free-form
    // movement (drag/wheel) is left exactly where the user releases it,
    // matching native scroll behavior, rather than auto-snapping to the
    // nearest card.
    const snapToNearestStep = (value) => {
        const step = getStepPx();
        return Math.round(value / step) * step;
    };

    applyOffset(0);

    prevButton.addEventListener('click', () => applyOffset(snapToNearestStep(offset) + getStepPx()));
    nextButton.addEventListener('click', () => applyOffset(snapToNearestStep(offset) - getStepPx()));

    let dragStartX = null;
    let dragStartOffset = 0;
    let dragDistance = 0;

    track.addEventListener('pointerdown', (event) => {
        // Reset unconditionally, before the button-guard check below —
        // pointerup always fires before the click it produces, so clearing
        // this on release (as a previous version of this code did) means
        // the click-suppression check below never sees a nonzero value for
        // the gesture that just ended. Resetting at the START of the next
        // press instead still clears a stale value left over from a prior
        // gesture (the original bug this guarded against), while leaving
        // the current gesture's own peak distance intact for its own click.
        dragDistance = 0;

        // Capturing the pointer unconditionally here — even when the press
        // starts on the "View Event" button itself — redirects how the
        // browser resolves the resulting click, so it can end up targeting
        // `track` instead of the button and never reach the button's own
        // listener at all. Skipping capture when the press starts on a
        // button lets it handle its own native click untouched; dragging
        // from anywhere else on the card still works normally.
        if (event.target.closest('button')) {
            return;
        }
        dragStartX = event.clientX;
        dragStartOffset = offset;
        track.classList.add('vf-event-carousel-dragging');
        track.setPointerCapture(event.pointerId);
    });

    track.addEventListener('pointermove', (event) => {
        if (dragStartX === null) {
            return;
        }
        // Track the peak distance reached during the whole gesture, not just
        // the current position's distance from the start — otherwise a drag
        // that moves far and then back near its start point (still a real
        // drag) reads as a small distance at release and slips past the
        // click-suppression check below.
        dragDistance = Math.max(dragDistance, Math.abs(event.clientX - dragStartX));
        applyOffset(dragStartOffset + (event.clientX - dragStartX));
    });

    // Deliberately does NOT snap: drag should rest exactly where released,
    // same as native two-finger trackpad scroll — not jump to the nearest
    // card.
    const endDrag = () => {
        dragStartX = null;
        track.classList.remove('vf-event-carousel-dragging');
    };
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);

    // This carousel moves via transform, not native overflow-x scrolling
    // (see comment above track), so a trackpad's natural horizontal swipe
    // needs to be translated into the same applyOffset() the drag/buttons
    // use. deltaX covers a real horizontal swipe; deltaY is the fallback
    // for a plain vertical mouse wheel. Not snapped, for the same reason
    // drag isn't: should behave exactly like native scroll.
    wrapper.addEventListener('wheel', (event) => {
        const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
        if (delta === 0) {
            return;
        }
        const offsetBeforeWheel = offset;
        applyOffset(offset - delta);
        if (offset !== offsetBeforeWheel) {
            event.preventDefault();
        }
    });

    let thumbDragStartX = null;
    let thumbDragStartOffset = 0;

    scrollbarThumb.addEventListener('pointerdown', (event) => {
        thumbDragStartX = event.clientX;
        thumbDragStartOffset = offset;
        scrollbarThumb.classList.add('vf-event-carousel-dragging');
        scrollbarThumb.setPointerCapture(event.pointerId);
    });

    scrollbarThumb.addEventListener('pointermove', (event) => {
        if (thumbDragStartX === null) {
            return;
        }
        const { maxOffsetMagnitude, barWidth, thumbWidth } = getScrollbarMetrics();
        const usableTrackWidth = Math.max(1, barWidth - thumbWidth);
        const dragDeltaX = event.clientX - thumbDragStartX;
        // Thumb moving right means the track should scroll further — i.e.
        // offset (already negative going right) becomes more negative —
        // hence the inversion here.
        const offsetDelta = -(dragDeltaX / usableTrackWidth) * maxOffsetMagnitude;
        applyOffset(thumbDragStartOffset + offsetDelta);
    });

    const endThumbDrag = () => {
        thumbDragStartX = null;
        scrollbarThumb.classList.remove('vf-event-carousel-dragging');
    };
    scrollbarThumb.addEventListener('pointerup', endThumbDrag);
    scrollbarThumb.addEventListener('pointercancel', endThumbDrag);

    // Suppresses the "View Event" click that would otherwise fire on
    // pointerup after a real swipe passes over a card.
    track.addEventListener('click', (event) => {
        if (dragDistance > RALLY_EVENT_CAROUSEL_DRAG_CLICK_SUPPRESSION_PX) {
            event.stopPropagation();
            event.preventDefault();
        }
    }, { capture: true });

    // ResizeObserver on the real panel container (rather than a window
    // resize listener) catches every case the panel's own size changes —
    // browser resize, but also e.g. the search dropdown itself expanding —
    // and self-disconnects once this particular carousel leaves the DOM.
    const panelEl = document.getElementById(RALLY_VOICEFLOW_CHAT_FRAME_ID);
    if (panelEl && window.ResizeObserver) {
        const observer = new ResizeObserver(() => {
            if (!wrapper.isConnected) {
                observer.disconnect();
                return;
            }
            wrapper.style.width = `${getPanelWidthPx(wrapper)}px`;
            applyOffset(offset);
        });
        observer.observe(panelEl);
    }

    return wrapper;
};

// memberId (not userId) is used as user_id here — it's the same auth.user.id
// value withTracker.js already uses for window.analytics.identify() app-wide,
// so it stays consistent with how every other event attributes to a person.
// userId here is actually user.claimedPlayer?.playerId (see index.jsx), a
// narrower value that's null/undefined for accounts without a claimed
// player — using it would silently drop identity for those users.
const createEventLinkClickHandler = ({ memberId, isPowered }) => (evt) => {
    // Nothing to attribute, navigate to, or tell the agent the user viewed
    // when there's no link — a link-less event is a true no-op, not a
    // degraded version of a real click-through.
    if (!evt.event_link) {
        return;
    }

    trackEvent(RallyEventRecEvents.RALLY_EVENT_LINK_CLICKED, {
        user_id: memberId,
        platform,
        user_type: isPowered ? SubscriptionTypes.POWER : SubscriptionTypes.FREE,
        event_id: evt.eventid,
    });

    // window.open must stay synchronous within this click handler (the real
    // user gesture) or most browsers block it as a pop-up.
    // Built via URL/URLSearchParams rather than string concatenation so a
    // link containing its own query string or #fragment still gets tagged
    // correctly — string-appending "?rally=1"/"&rally=1" would land inside
    // the fragment (or collide with an existing ?rally= param) whenever one
    // is present.
    // ?rally=1 tags the new tab as Rally-attributed, read by the event
    // page (DE-2704) to attribute checkout/registration telemetry back
    // to this click. new URL() can throw on a genuinely malformed
    // event_link (backend data this file doesn't control) — fall back to
    // opening the raw, untagged link rather than losing the click entirely.
    let taggedLink = evt.event_link;
    try {
        const url = new URL(evt.event_link, window.location.href);
        url.searchParams.set('rally', '1');
        taggedLink = url.toString();
    } catch (error) {
        // Untagged navigation still beats no navigation.
        // eslint-disable-next-line no-console
        console.warn('Could not parse event_link as a URL, opening it untagged', error);
    }
    window.open(taggedLink, '_blank', 'noopener,noreferrer');

    // A custom action type isn't surfaced to the agent as readable input — a
    // "text" action is what gets treated as if the user typed it, becoming
    // last_utterance the playbook instructions can match against.
    if (window.voiceflow?.chat?.interact) {
        window.voiceflow.chat.interact({
            type: 'text',
            payload: `View Event: ${evt.eventname}`,
        });
    }
};

const createEventCarouselExtension = ({ isPowered, onViewEventClick } = {}) => ({
    name: 'EventCarousel',
    type: 'response',
    match: ({ trace }) => trace.type === RALLY_EVENT_CAROUSEL_TRACE_TYPE,
    render: ({ trace, element }) => {
        const events = trace.payload?.events || [];
        createEventCarousel(events, element, { isPowered, onViewEventClick });
    },
});

export const initializeVoiceflowEmbeddedChat = async ({
    target,
    userId,
    memberId,
    firstName,
    lastName,
    isPowered,
    subscriptionState,
    subscriptionGateway,
    subscriptionTierType,
    location,
    projectId,
    versionId = undefined,
    onClose,
    onOpenPaymentModal = null,
    onOpenSubscriptionPage = null,
    existingObserver = null,
    setObserver,
    autostart = true,
}) => {
    await loadVoiceflowScript();

    if (!target) {
        return existingObserver;
    }

    target.innerHTML = '';
    const normalizedUserId = String(userId);

    const extensions = [
        ...(onOpenPaymentModal ? [createRallyPaymentExtension(onOpenPaymentModal)] : []),
        ...(onOpenSubscriptionPage ? [createRallySubscriptionExtension(onOpenSubscriptionPage)] : []),
        createEventCarouselExtension({
            isPowered,
            onViewEventClick: createEventLinkClickHandler({ memberId, isPowered }),
        }),
        createEventRecommendationsTelemetryExtension({ memberId, isPowered, location }),
    ];

    window.voiceflow.chat.load({
        verify: { projectID: projectId },
        url: VOICEFLOW_RUNTIME_URL,
        userID: normalizedUserId,
        // Omitting versionID routes to the published flow; 'development' targets the sandbox.
        ...(versionId ? { versionID: versionId } : {}),
        render: {
            mode: 'embedded',
            target,
        },
        autostart,
        launch: {
            event: {
                type: 'launch',
                payload: {
                    player_id: Number(userId),
                    member_id: Number(memberId),
                    platform_type: 'web',
                    first_name: firstName,
                    last_name: lastName,
                    is_powered: isPowered,
                    subscription_state: subscriptionState,
                    subscription_gateway: subscriptionGateway || null,
                    subscription_tier_type: subscriptionTierType || null,
                },
            },
        },
        voice: {
            url: VOICEFLOW_VOICE_URL,
        },
        // Voiceflow assistant styles
        // remove box-shadow and overflow: hidden from the assistant
        // hide the default title text (replaced via DOM injection in injectVoiceflowHeaderTitle)
        // .vfrc-chat {box-shadow: none !important; overflow: hidden !important;}.vfrc-header--title {font-size: 0 !important;}
        assistant: {
            stylesheet: 'data:text/css;base64,LnZmcmMtY2hhdCB7Ym94LXNoYWRvdzogbm9uZSAhaW1wb3J0YW50OyBvdmVyZmxvdzogaGlkZGVuICFpbXBvcnRhbnQ7fS52ZnJjLWhlYWRlci0tdGl0bGUge2ZvbnQtc2l6ZTogMCAhaW1wb3J0YW50O30=',
            extensions,
        },
    });

    if (window.voiceflow?.chat?.hide) {
        window.voiceflow.chat.hide();
    }

    injectVoiceflowHeaderTitle({ target });

    return injectVoiceflowHeaderCloseButton({
        target,
        onClose,
        existingObserver,
        setObserver,
    });
};
